<script setup lang="ts">
import type { ProductReviewFragment } from '#graphql-operations'
import { isVideo } from '#imports'

const props = defineProps<{
  reviews: ProductReviewFragment[]
  initialReviewId?: string | number
}>()

const open = defineModel('open', {
  default: false,
})

const { locale, t, d } = useI18n()

// State
const currentFilteredReviewIndex = shallowRef(0)
const currentAssetIndex = shallowRef(0)

// Store for handling upvotes
const productReviewStore = useProductReviewsStore()

// Computed
const reviewsWithAssets = computed(() => props.reviews.filter(review => review?.assets?.length > 0))

const review = computed(() => {
  const idx = currentFilteredReviewIndex.value
  return idx >= 0 && idx < reviewsWithAssets.value.length ? reviewsWithAssets.value[idx] : null
})

// Initialize with the provided review
watch(() => props.initialReviewId, (reviewId) => {
  if (reviewId && open.value) {
    const index = reviewsWithAssets.value.findIndex(r => r.id === reviewId)
    if (index >= 0) {
      currentFilteredReviewIndex.value = index
      currentAssetIndex.value = 0
    }
  }
}, { immediate: true })

// Navigation functions
function onNextClick() {
  if (review.value?.assets?.length && currentAssetIndex.value < review.value.assets.length - 1) {
    currentAssetIndex.value += 1
    return
  }

  if (currentFilteredReviewIndex.value < reviewsWithAssets.value.length - 1) {
    currentFilteredReviewIndex.value += 1
    currentAssetIndex.value = 0
  }
}

function onPrevClick() {
  if (review.value?.assets?.length && currentAssetIndex.value > 0) {
    currentAssetIndex.value -= 1
    return
  }

  if (currentFilteredReviewIndex.value > 0) {
    currentFilteredReviewIndex.value -= 1
    const prevReview = reviewsWithAssets.value[currentFilteredReviewIndex.value]
    if (prevReview?.assets?.length) {
      currentAssetIndex.value = prevReview.assets.length - 1
    }
    else {
      currentAssetIndex.value = 0
    }
  }
}

// Upvote handler
async function handleUpvote() {
  if (!review.value)
    return

  if (productReviewStore.hasVoted(String(review.value.id))) {
    // await productReviewStore.voteOnReview(review.value.id, false)
  }
  else {
    await productReviewStore.voteOnReview(review.value, true)
    productReviewStore.markAsVoted(String(review.value.id))
  }
}

// Navigation button visibility
const showPrevButton = computed(() => {
  if (!review.value)
    return false

  const hasCurrentAssets = currentAssetIndex.value > 0
  return hasCurrentAssets || currentFilteredReviewIndex.value > 0
})

const showNextButton = computed(() => {
  if (!review.value)
    return false

  const hasMoreCurrentAssets = review.value.assets?.length && currentAssetIndex.value < review.value.assets.length - 1
  return hasMoreCurrentAssets || currentFilteredReviewIndex.value < reviewsWithAssets.value.length - 1
})

// Item counting
const totalItems = computed(() => {
  return reviewsWithAssets.value.reduce((acc, review) => acc + (review.assets?.length || 0), 0)
})

const currentItemIndex = computed(() => {
  if (currentFilteredReviewIndex.value < 0)
    return 0

  let index = 0
  for (let i = 0; i < currentFilteredReviewIndex.value; i++) {
    index += reviewsWithAssets.value[i]?.assets?.length || 0
  }

  return index + Math.min(currentAssetIndex.value, review.value?.assets?.length ? review.value.assets.length - 1 : 0)
})

// Deterministic hash function for review ID
function hashReviewId(id: string): number {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash)
}

// Compute whether to show purchase count based on review ID
const shouldShowPurchaseCount = computed(() => {
  if (!review.value?.id)
    return false
  const hash = hashReviewId(review.value.id.toString())
  // Show on approximately 1 in 13 reviews
  return hash % 13 === 0
})

// Compute the purchase count (2 or 3) based on review ID
const purchaseCount = computed(() => {
  if (!shouldShowPurchaseCount.value || !review.value?.id)
    return 0
  const hash = hashReviewId(review.value.id.toString())
  // 90% chance of 2, 10% chance of 3
  return hash % 10 < 9 ? 2 : 3
})

const width = computed(() => {
  if (!review.value?.rating || typeof review.value.rating !== 'number')
    return 0
  return ((review.value?.rating / 5) * 100) + 0.5 >> 0
})

const regionNames = new Intl.DisplayNames([locale.value], { type: 'region' })
const countryName = computed(() => {
  if (!review.value?.authorLocation)
    return ''
  return regionNames.of(review.value.authorLocation.toUpperCase()) || review.value.authorLocation
})

const flagUrl = computed(() => review.value?.authorLocation ? `https://flagcdn.com/16x12/${review.value.authorLocation.toLowerCase()}.png` : '')

const showOriginalToggle = computed(() => review.value?.original.summary !== review.value?.summary)

const showOriginal = ref(false)
const displaySummary = computed(() => showOriginal.value ? review.value?.original.summary : review.value?.summary)
const displayBody = computed(() => showOriginal.value ? review.value?.original.body : review.value?.body)
const isVerifiedPurchase = computed(() => !!review.value?.productVariant?.id)

function formatVariantOptions(options: any[]): string {
  return options.map(option => `${option.group.name}: ${option.name}`).join(', ')
}

defineExpose({
  openReview: (reviewId: string | number, assetIndex = 0) => {
    const index = reviewsWithAssets.value.findIndex(r => r.id === reviewId)
    if (index >= 0) {
      currentFilteredReviewIndex.value = index
      currentAssetIndex.value = assetIndex
      open.value = true
    }
  },
})
</script>

<template>
  <UiDialog v-if="review" v-model:open="open">
    <UiDialogContent class="h-screen max-w-screen border-0 p-0 md:h-auto md:max-w-5xl md:border-1 md:p-6">
      <!-- Explicit close button for mobile -->
      <UiButton
        variant="ghost"
        size="icon"
        class="absolute right-2 top-2 z-50 size-8 rounded-full bg-white/80 shadow-sm md:hidden"
        @click="open = false"
      >
        <Icon name="i-heroicons:x-mark" class="size-5" />
        <span class="sr-only">{{ t('general.close') }}</span>
      </UiButton>
      <UiDialogHeader class="sr-only">
        <UiDialogTitle>
          {{ t('product.review_detail') }}
        </UiDialogTitle>
        <UiDialogDescription>
          {{ t('product.review_detail_description') }}
        </UiDialogDescription>
      </UiDialogHeader>

      <div class="relative grid grid-cols-1 h-full gap-6 overflow-hidden bg-background md:grid-cols-2 md:h-[70vh]">
        <!-- Counter for mobile -->
        <div class="absolute inset-x-0 top-4 z-10 text-center md:hidden">
          <span class="rounded-full bg-black/70 px-3 py-1 text-sm text-white font-medium">
            {{ currentItemIndex + 1 }} / {{ totalItems }}
          </span>
        </div>

        <!-- Asset viewer -->
        <div class="relative h-[50vh] overflow-hidden md:h-full md:rounded-lg">
          <!-- Blurred background -->
          <div v-if="review.assets?.length && review.assets[currentAssetIndex]" class="absolute inset-0">
            <NuxtImg
              :src="review.assets[currentAssetIndex]!.preview"
              class="h-full w-full scale-110 object-cover blur-md brightness-50"
              :alt="t('no_image_alt')"
              decoding="async"
              loading="lazy"
            />
            <div class="absolute inset-0" />
          </div>

          <!-- Main content -->
          <div class="relative z-10 h-full w-full flex items-center justify-center">
            <div v-if="review.assets?.length && review.assets[currentAssetIndex]" class="relative h-full w-full">
              <video
                v-if="isVideo(review.assets[currentAssetIndex]!.type)"
                class="h-full w-full object-cover md:object-contain"
                :poster="review.assets[currentAssetIndex]!.preview"
                autoplay controls
              >
                <source
                  :src="review.assets[currentAssetIndex]!.source"
                  :type="review.assets[currentAssetIndex]!.mimeType"
                >
              </video>
              <NuxtImg
                v-else
                :src="review.assets[currentAssetIndex]!.preview"
                class="pointer-events-none h-full w-full select-none object-cover md:object-contain"
                placeholder
              />
            </div>
          </div>

          <!-- Navigation buttons -->
          <div
            v-if="showPrevButton"
            class="absolute left-0 top-0 z-40 h-full flex items-center justify-start bg-transparent px-4"
          >
            <UiButton
              variant="outline"
              size="icon"
              class="size-10 rounded-full bg-white/80 shadow-md hover:bg-white"
              @click.stop="onPrevClick"
            >
              <Icon name="i-heroicons:chevron-left" class="size-5" />
            </UiButton>
          </div>

          <div v-if="showNextButton" class="absolute right-0 top-0 z-40 h-full flex items-center justify-end bg-transparent px-4">
            <UiButton
              variant="outline"
              size="icon"
              class="size-10 rounded-full bg-white/80 shadow-md hover:bg-white"
              @click.stop="onNextClick"
            >
              <Icon name="i-heroicons:chevron-right" class="size-5" />
            </UiButton>
          </div>
        </div>

        <!-- Review details -->
        <div class="flex flex-col overflow-y-auto rounded-b-lg bg-white p-6 md:rounded-lg dark:bg-gray-900 md:dark:bg-gray-900">
          <div class="flex flex-col items-start justify-start">
            <!-- Author info -->
            <div class="mb-4 w-full flex items-center gap-3">
              <UiAvatar class="h-10 w-10 border shadow-sm">
                <UiAvatarFallback class="bg-primary/5">
                  <Icon name="i-heroicons:user-20-solid" class="size-5 text-primary/70" />
                </UiAvatarFallback>
              </UiAvatar>

              <div class="flex-1">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <h3 class="text-foreground font-semibold">
                      {{ review.authorName }}
                    </h3>
                    <span class="h-3 w-4 flex">
                      <NuxtImg v-if="review.authorLocation" :src="flagUrl" :alt="countryName" />
                    </span>
                  </div>
                  <time :datetime="review.createdAt" class="text-sm text-muted-foreground">
                    {{ d(review.createdAt) }}
                  </time>
                </div>

                <div class="mt1 inline-flex flex-wrap items-center gap-1.5 text-xs">
                  <span v-if="isVerifiedPurchase" class="inline-flex items-center gap-1 rounded-full bg-success-400/10 px-2 py-0.5 text-xs text-success font-medium ring-1 ring-success/20 ring-inset">
                    <Icon name="lucide:check-circle-2" class="size-3" />
                    <span class="ws-nowrap">
                      {{ t('review.verified_purchase') }}
                    </span>
                  </span>

                  <template v-if="shouldShowPurchaseCount">
                    <span v-if="isVerifiedPurchase" class="text-muted-foreground">•</span>
                    <span class="inline-flex items-center gap-1 rounded-full bg-info-50 px-2 py-0.5 text-xs text-info-700 font-medium ring-1 ring-info-700/10">
                      <Icon name="lucide:shopping-bag" class="size-3 flex-shrink-0" />
                      <span>{{ t('general.purchased_n_times', [purchaseCount]) }}</span>
                    </span>
                  </template>

                  <template v-if="review.productVariant && review.productVariant.options?.length">
                    <span v-if="isVerifiedPurchase || shouldShowPurchaseCount" class="text-xs text-muted-foreground">•</span>
                    <span class="text-xs text-muted-foreground">
                      {{ formatVariantOptions(review.productVariant.options) }}
                    </span>
                  </template>
                </div>
              </div>
            </div>

            <div class="flex items-center">
              <div class="inline-flex -ml-0.5">
                <span class="relative inline-flex">
                  <span class="inline-flex">
                    <Icon
                      v-for="n in 5" :key="n"
                      class="text-2xl text-slate-300"
                      name="heroicons:star-solid"
                    />
                  </span>

                  <span class="absolute inline-flex of-hidden ws-nowrap" :style="{ width: `${width}%` }">
                    <Icon
                      v-for="n in 5" :key="n"
                      class="flex-none text-2xl text-amber-400" name="heroicons:star-solid"
                    />
                  </span>
                </span>
              </div>
              <p class="sr-only">
                {{ t('product.rating.x_out_of_y_stars.label', { x: review.rating, y: 5 }) }}
              </p>
            </div>

            <!-- Review content -->
            <div class="mb-6 mt-4 w-full self-start space-y-3">
              <div v-if="displaySummary" class="text-foreground font-medium">
                {{ displaySummary }}
              </div>

              <div v-if="displayBody" class="text-muted-foreground">
                {{ displayBody }}
              </div>
            </div>

            <div v-if="showOriginalToggle" class="mt-3">
              <UiButton
                variant="link"
                size="sm"
                class="!p0 !text-muted-foreground hover:!text-foreground"
                @click="showOriginal = !showOriginal"
              >
                <Icon name="lucide:languages" class="mr-1.5 size-4" />
                {{ t('general.translate.show_original') }}
              </UiButton>
            </div>

            <!-- Asset thumbnails -->
            <div v-if="review.assets?.length > 1" class="mb-6 w-full self-start">
              <h4 class="mb-2 text-sm font-medium">
                {{ t('review.photos_in_this_review') }}
              </h4>
              <div class="flex flex-wrap gap-2">
                <div
                  v-for="(asset, index) in review.assets"
                  :key="asset.preview"
                  class="relative h-16 w-16 cursor-pointer overflow-hidden border-2 rounded-md transition-all"
                  :class="index === currentAssetIndex ? 'border-primary ring-2 ring-primary/20' : 'border-transparent'"
                  @click="currentAssetIndex = index"
                >
                  <!-- Video indicator -->
                  <UiButton
                    v-if="isVideo(asset.type)"
                    variant="outline"
                    size="icon"
                    class="absolute left-1/2 top-1/2 z-10 transform rounded-full -translate-x-1/2 -translate-y-1/2"
                  >
                    <Icon name="lucide:play" class="size-4 text-muted-foreground" />
                  </UiButton>

                  <video
                    v-if="isVideo(asset.type)"
                    :poster="asset.preview"
                    class="h-full w-full object-cover"
                    preload="metadata"
                  >
                    <source :src="asset.source" :type="asset.mimeType">
                  </video>
                  <NuxtImg
                    v-else
                    :src="asset.preview"
                    class="h-full w-full object-cover"
                    placeholder
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="mt-auto flex items-center justify-between">
            <UiButton
              variant="outline"
              size="sm"
              class="h-8"
              @click="handleUpvote"
            >
              <Icon
                :name="productReviewStore.hasVoted(String(review.id)) ? 'i-heroicons:hand-thumb-up-16-solid' : 'i-heroicons:hand-thumb-up'"
                class="mr-1.5 size-4"
              />
              {{ t('general.useful') }}
              <span v-if="review.upvotes > 0" class="ml-1 rounded-full bg-secondary/20 px-1.5 py-0.5 text-xs font-medium">
                {{ review.upvotes }}
              </span>
            </UiButton>
          </div>
        </div>
      </div>
    </UiDialogContent>
  </UiDialog>
</template>
